import React, { Component } from 'react';
import { computed, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';

import { Button, Grid } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoanAccount, ILoanAccountContact } from '../../../interfaces';
import { SessionStoreClass } from '../../../stores';
import { Icon, Loader } from '../../common';
import SmartBool from '../../../utils/SmartBool';

import AddContactModal from './AddContactModal';
import ContactFormModal from './ContactFormModal';
import ContactsTable from './ContactsTable';

const { PERMISSIONS } = AppConstants;

interface IProps {
  loanAccount: ILoanAccount;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

/**
 * Who to invoice and who pays. Modelled on the case contacts tab, over the account's own contacts:
 * assigning a contact and unassigning it are writes to the contact, not to the account, so everything
 * here goes through the one endpoint.
 */
@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class TabContacts extends Component<IProps> {
  @observable private contacts: ILoanAccountContact[] = [];
  @observable private isLoading = true;
  @observable private showAddModal = new SmartBool();
  // The contact being edited, or null when that modal is closed
  @observable private editingContact: null | ILoanAccountContact = null;

  public componentDidMount () {
    this.fetchContacts();
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchContacts ({ isRefresh = false } = {}) {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    // A refresh leaves the table up while it runs. Every write here goes through one, and swapping the
    // rows for a full-height loader makes the tab flash for the length of the round trip. c.f.
    // TabCompliance, which holds its tables up through a re-read for the same reason.
    this.isLoading = !isRefresh;

    try {
      // Every contact on the account rather than a page of them, as the loans and charges tables do
      this.contacts = await Clients.loanAccountContacts.listAll({ params: { account: loanAccount.id } });
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error fetching the account contacts:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  private get canAdd () {
    // The add flow's own step can assign an existing contact, which is a change rather than an add. A
    // user holding one permission without the other is not a combination the groups hand out, so the
    // button follows the one the flow is named for and the API refuses the rest.
    return this.injected.SessionStore.userHasPermission(PERMISSIONS.ADD_LOAN_ACCOUNT_CONTACT);
  }

  private get canChange () {
    return this.injected.SessionStore.userHasPermission(PERMISSIONS.CHANGE_LOAN_ACCOUNT_CONTACT);
  }

  /**
   * react-table wants a plain array, not the observable one mobx made of the response. Cloned here
   * rather than at the point of use so the array keeps its identity between renders -- see the same
   * note on TabCompliance.
   */
  @computed
  private get contactData () {
    return toJS(this.contacts);
  }

  private closeAddModal () {
    this.showAddModal.setFalse();
  }

  private closeEditModal () {
    this.editingContact = null;
  }

  private onEditContact (contactId: string) {
    this.editingContact = this.contacts.find(contact => contact.id === contactId) || null;
  }

  // A new contact names the account it is being added to, so one request both creates and assigns it
  private async onCreateContact (data: object) {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    await Clients.loanAccountContacts.create({ ...data, add_to_account: loanAccount.id });
    await this.fetchContacts({ isRefresh: true });
  }

  /**
   * Assigning and unassigning each name the one account that changed, rather than writing back the list
   * the contact is on. The list this page read could be out of date by now, and sending it as though it
   * were current would take the contact off an account another user had just put it on.
   */
  private async onAssignContact (contact: ILoanAccountContact) {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    await Clients.loanAccountContacts.update(contact.id, { add_to_account: loanAccount.id });

    this.closeAddModal();
    await this.fetchContacts({ isRefresh: true });
  }

  private async onUpdateContact (data: object) {
    const { Clients } = this.injected
      // istanbul ignore next: the modal only renders with a contact behind it
      , contactId = this.editingContact ? this.editingContact.id : ''
      ;

    await Clients.loanAccountContacts.update(contactId, data);
    await this.fetchContacts({ isRefresh: true });
  }

  /**
   * Removing takes the contact off this account and leaves the contact itself alone -- it may be on
   * others, and the funder keeps it either way for the add flow to find again. c.f. removing a contact
   * from a case, which does the same from the case's side.
   */
  private async onRemoveContact (contactId: string) {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    await Clients.loanAccountContacts.update(contactId, { remove_from_account: loanAccount.id });
    await this.fetchContacts({ isRefresh: true });
  }

  private renderAddButton () {
    if (!this.canAdd) { return null; }

    return (
      // Link-style rather than the solid button the loans and compliance tables use, matching the case
      // contacts table this tab is modelled on
      <Button bsStyle='link' className='btn-add-contact' onClick={this.showAddModal.setTrue}>
        <Icon type='plus-square-o' /> Add New Contact
      </Button>
    );
  }

  private renderContent () {
    if (this.isLoading) {
      return <Loader className='page-loader' logo />;
    }

    return (
      <ContactsTable
        canChange={this.canChange}
        contacts={this.contactData}
        newButton={this.renderAddButton()}
        onEdit={this.onEditContact}
        onRemove={this.onRemoveContact}
      />
    );
  }

  public render () {
    const { loanAccount } = this.props;

    return (
      <Grid fluid className='loan-account-detail-tab tab-contacts'>
        {this.renderContent()}

        {this.showAddModal.isTrue && (
          <AddContactModal
            loanAccountId={loanAccount.id}
            onAssign={this.onAssignContact}
            onClose={this.closeAddModal}
            onCreate={this.onCreateContact}
          />
        )}

        {!!this.editingContact && (
          <ContactFormModal
            contact={this.editingContact}
            isEditing
            onClose={this.closeEditModal}
            onSave={this.onUpdateContact}
          />
        )}
      </Grid>
    );
  }
}

export default TabContacts;
